/**
 * WeeklyLessonCard — Gap 1: Curso passo-a-passo.
 *
 * Card destaque no PhaseExecute com a aula da semana. Tap → expand para
 * ler todo o conteúdo. Botão "Marcar como lida" que persiste em
 * `user_lesson_progress`.
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, ChevronRight, Check, Clock } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function WeeklyLessonCard({ program }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/programs/${program}/lesson-today`, { headers: headers() });
      const d = await res.json();
      setData(d);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [program]);

  const markRead = async (e) => {
    e.stopPropagation();
    setMarkingRead(true);
    try {
      const res = await fetch(`${API}/api/programs/${program}/lesson-complete`, {
        method: "POST", headers: headers(), body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error();
      toast.success("Aula concluída ✓");
      await load();
    } catch {
      toast.error("Erro a gravar");
    } finally {
      setMarkingRead(false);
    }
  };

  if (loading || !data?.lesson?.content_ready) return null;

  const L = data.lesson;
  const done = !!data.completed;
  const color = L.category_color || "#8b5cf6";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border overflow-hidden mb-4"
      style={{
        borderColor: `${color}55`,
        background: `linear-gradient(140deg, ${color}18 0%, rgba(9,9,11,0.95) 60%)`,
        boxShadow: `0 6px 22px ${color}20, inset 0 1px 0 ${color}25`,
      }}
      data-testid="weekly-lesson-card"
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4"
        data-testid="weekly-lesson-toggle"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
            style={{
              background: `radial-gradient(circle, ${color}40 0%, ${color}10 70%)`,
              boxShadow: `0 0 18px ${color}55, inset 0 0 12px ${color}30`,
            }}
          >
            {L.category_icon || <BookOpen className="w-5 h-5" style={{ color }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>
                Aula da semana {data.absolute_week}/{data.total_weeks}
              </p>
              {done && (
                <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 flex items-center gap-1">
                  <Check className="w-2.5 h-2.5" /> Lida
                </span>
              )}
            </div>
            <p className="text-sm font-black text-zinc-100 leading-tight mt-0.5">{L.title_pt}</p>
            <p className="text-[11px] text-zinc-400 mt-1 leading-snug italic">"{L.teaser_pt}"</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {L.reading_time_min || 5} min de leitura
              </span>
              <ChevronRight className={`w-3.5 h-3.5 text-zinc-500 ml-auto transition-transform ${expanded ? "rotate-90" : ""}`} />
            </div>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t"
            style={{ borderColor: `${color}30` }}
          >
            <div className="p-4 pt-3 max-h-[420px] overflow-y-auto">
              <div
                className="text-[13px] text-zinc-200 leading-relaxed whitespace-pre-wrap prose-invert"
                data-testid="weekly-lesson-content"
              >
                {L.content_pt || "Conteúdo em preparação."}
              </div>
              {!done && (
                <button
                  onClick={markRead}
                  disabled={markingRead}
                  className="mt-4 w-full h-11 rounded-lg text-black text-sm font-bold active:scale-[0.98] disabled:opacity-50"
                  style={{
                    background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                    boxShadow: `0 4px 18px ${color}55`,
                  }}
                  data-testid="weekly-lesson-mark-read"
                >
                  {markingRead ? "A gravar..." : "Marcar como lida ✓"}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
